import React, { useState } from 'react';

import { Input, InputProps } from '@rbilabs/universal-components';

import { primitive } from 'styles/constants';

type PasteEvent = {
  clipboardData?: { getData: (type: string) => string };
  preventDefault?: () => void;
};

const StyledInput = Input.withConfig(props => ({
  maxWidth: '84px',
  minHeight: '50px',
  borderRadius: primitive.$borderRadius,
  borderWidth: '1px',
  borderColor: primitive.$greyFive,
  textAlign: 'center',
  color: primitive.$bbqBrown,
  ...props,
}));

const StyledInputWithPaste = StyledInput as React.ComponentType<
  React.ComponentProps<typeof StyledInput> & {
    onPaste?: (event: PasteEvent) => void;
  }
>;

interface FieldProps extends InputProps {
  autoFocus?: boolean;
  focusNext?: () => void;
  focusBack?: () => void;
  initialValue?: string;
  maxLength?: number;
  name: string;
  onFieldChange?: (inputName: string, inputText: string) => void;
  onPasteText?: (inputName: string, pastedText: string) => void;
  setRef?: (instance: any) => void;
  value?: string;
}

export const BasicInput = ({
  autoFocus = false,
  name,
  focusNext,
  focusBack,
  initialValue = '',
  keyboardType = 'default',
  maxLength,
  onFieldChange,
  onPasteText,
  setRef,
  type = 'text',
  value: valueProp,
  ...props
}: FieldProps) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue);
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : uncontrolledValue;
  // Parent owns combined-length paste; native maxLength would truncate first.
  const deferLengthToParent = Boolean(onPasteText);

  const onInputChange = (inputText: string) => {
    if (!deferLengthToParent && maxLength && inputText.length > maxLength) {
      return;
    }

    if (!isControlled) {
      setUncontrolledValue(inputText);
    }
    onFieldChange?.(name, inputText);

    if (inputText.length === maxLength && focusNext) {
      focusNext();
    }

    if (!inputText.length && focusBack) {
      focusBack();
    }
  };

  const handlePaste = (event: PasteEvent) => {
    const pastedText = event.clipboardData?.getData('text');

    if (pastedText == null || !onPasteText) {
      return;
    }

    event.preventDefault?.();
    onPasteText(name, pastedText);
  };

  return (
    <StyledInputWithPaste
      value={value}
      onChangeText={onInputChange}
      // NativeBase input types omit DOM paste; web still needs it so maxLength does not truncate first.
      onPaste={onPasteText ? handlePaste : undefined}
      type={type}
      keyboardType={keyboardType}
      maxLength={deferLengthToParent ? undefined : maxLength}
      autoFocus={autoFocus}
      ref={setRef}
      {...props}
    />
  );
};
